/**
 * 通用 API 客户端（middleware 链重构版）。
 *
 * - 开发环境使用相对路径，经 Vite `/api` 代理进入 BFF；生产环境读取 `VITE_API_BASE`
 * - 业务代码统一用 `client.get/post/patch/put/delete`；签名与旧 client.ts 完全兼容
 * - 内部是 middleware 链：`[auth, refresh, parse]`；qa/runtime 在 VITE_QA=1 时
 *   会通过 `_installTelemetry()` 在 refresh 与 parse 之间追加 telemetry middleware
 * - access_token 存 `sessionStorage('fp_access')`；refresh_token 存 `localStorage('fp_refresh')`
 * - 401 → refresh 一次后重放（refresh middleware 负责）
 * - `{ ok: true, data }` 解包 / `{ ok: false, error }` → ApiError（parse middleware + createClient 负责）
 *
 * 设计边界：
 *  - 业务侧 `import { client, ApiError, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, Method } from './client'`
 *    全部保留；旧 ApiClient 类实例化 API 已被替换为 middleware 链。
 *  - `_installTelemetry()` 通过 live-binding 替换 `client` 指向的实例；
 *    ES module 的 `export let` 让所有 importer 自动看到新值。
 */

import { createApiClient, resolveBaseUrl } from './createClient';
import { authMiddleware } from './middleware/auth';
import { createParseMiddleware } from './middleware/parse';
import { createRefreshMiddleware } from './middleware/refresh';
import type { ApiClient, Middleware } from './middleware/types';
import { ApiError } from './apiError';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from './tokens';

export type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export { ApiError, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY };
export type { ApiClient };

/**
 * 构造默认 middleware 链：[auth, refresh, parse]。
 * 不包含 telemetry —— `_installTelemetry()` 在 refresh 与 parse 之间再追加 telemetry。
 */
function defaultMiddleware(baseUrl: string): Middleware[] {
  return [
    authMiddleware,
    createRefreshMiddleware({ baseUrl: () => baseUrl }),
    createParseMiddleware({ baseUrl: () => baseUrl }),
  ];
}

/**
 * `export let` 让所有 importer 拿到 live binding —— `_installTelemetry()` 替换
 * 后业务侧无需任何修改，自动走新链。
 */
export let client: ApiClient = createApiClient({
  baseUrl: resolveBaseUrl(),
  middleware: defaultMiddleware(resolveBaseUrl()),
});

/**
 * 由 main.tsx（VITE_QA=1 时）调用，把 telemetry middleware 拼到 refresh 与 parse 之间。
 * 业务代码不需要感知；下次 `client.get/post/...` 自动走新链。
 */
export function _installTelemetry(): void {
  // 动态 import 避免 SSR / 测试环境拉起 qa/runtime。
  // main.tsx 会保证只在 VITE_QA=1 时调一次。
  void import('../../../qa/runtime/browser/register')
    .then(({ registerRuntimeQa }) => {
      const base = resolveBaseUrl();
      // 用 fresh 的 refresh/parse 实例（避免闭包被旧 client 污染）。
      const refresh = createRefreshMiddleware({ baseUrl: () => base });
      const parse = createParseMiddleware({ baseUrl: () => base });
      const existing: Middleware[] = [authMiddleware, refresh];
      const next = registerRuntimeQa([...existing, parse]);
      client = createApiClient({ baseUrl: base, middleware: next });
    })
    .catch(() => {
      /* qa 模块不可用时静默退化为原链路 */
    });
}
